// Implements: F-MCK-014, F-MCK-021, F-VER-004
/**
 * The card menu's mockup v3 actions (P4: Duplicar, Transferir, Exportar) and the dialog they open.
 *
 * Duplicar and Exportar act at once (`useDuplicateMockup`, `useExportMockupVersion`); Transferir
 * opens `TransferOwnershipDialog`, keyed by the mockup id so a choice never lands on another card
 * (checklist 14). The gallery spreads `actions` into its own and renders `dialogs` once.
 */

import { useState } from "react";
import type { ReactNode } from "react";

import type { MockupSummary } from "../../../types/api";
import type { MockupActions } from "../components/mockupActions";
import { useDuplicateMockup } from "./useDuplicateMockup";
import { useExportMockupVersion } from "./useExportMockupVersion";
import { TransferOwnershipDialog } from "./TransferOwnershipDialog";

export interface MockupMenuActions {
  /** Spread into the gallery's `MockupActions`. */
  readonly actions: Partial<Pick<MockupActions, "onDuplicate" | "onTransfer" | "onExport">>;
  /** Rendered once by the gallery, after its own dialogs. */
  readonly dialogs: ReactNode;
}

export function useMockupMenuActions(): MockupMenuActions {
  const duplicate = useDuplicateMockup();
  const exportVersion = useExportMockupVersion();
  const [transferring, setTransferring] = useState<MockupSummary | null>(null);

  return {
    actions: {
      onDuplicate: (mockup) => {
        void duplicate(mockup.id);
      },
      onTransfer: setTransferring,
      onExport: (mockup, kind) => {
        void exportVersion(mockup.id, kind);
      },
    },
    dialogs:
      transferring === null ? null : (
        <TransferOwnershipDialog
          key={transferring.id}
          mockupId={transferring.id}
          mockupName={transferring.name}
          organizationId={transferring.organization_id}
          ownerId={transferring.owner_id}
          personal={transferring.scope_kind === "personal"}
          onClose={() => {
            setTransferring(null);
          }}
        />
      ),
  };
}
